import React from "react";
import PropTypes from "prop-types";
import { useSeriesDetail } from "./useForecastExplorer";
import ForecastChart from "../../forecast/ForecastChart";
import DriverWaterfall from "./DriverWaterfall";
import { fmtEur, fmtEurSigned, fmtSmape, unitDisplay, formatSeriesName } from "./format";

// contributorLabel turns a contributor entry into a readable label + a plain-
// language hint. Series-detail contributors are keyed by usage unit (c.unit),
// so we explain what that unit means (Storage / Compute / Requests).
function contributorLabel(c) {
  return unitDisplay(c.source ?? c.unit ?? c.sku ?? "Unknown");
}

// summaryFromSeries computes projected_spend and growth_eur from the detail
// series array if row-level figures are not passed as props.
function summaryFromSeries(series) {
  if (!series || series.length === 0) return { projected: null, growth: null };
  const forecasted = series.filter((d) => d.forecast != null);
  const actual = series.filter((d) => d.actual != null);
  const projected = forecasted.reduce((s, d) => s + (d.forecast || 0), 0) || null;
  const baseActual = actual.length > 0 ? actual.reduce((s, d) => s + (d.actual || 0), 0) : null;
  const growth = projected != null && baseActual != null ? projected - baseActual : null;
  return { projected, growth };
}

export default function SeriesDetail({ name, horizon, data, isLoading }) {
  // The shell passes data/isLoading from useSeriesDetail directly; we also
  // accept a hook-less usage where we fetch ourselves when data is not passed.
  const ownQuery = useSeriesDetail(!data && !isLoading && name ? name : null, horizon);

  const effectiveData = data ?? ownQuery.data ?? null;
  const effectiveLoading = isLoading ?? ownQuery.isLoading ?? false;

  // Gentle prompt when no series selected
  if (!name) {
    return (
      <div className="flex flex-col items-center justify-center rounded-card border border-dashed border-border bg-surface-2 py-16 text-center">
        <span className="text-3xl" role="img" aria-label="chart">
          📈
        </span>
        <p className="mt-3 text-sm font-medium text-text-2">Select a series</p>
        <p className="mt-1 text-xs text-text-3">
          Click any row in the table to see the forecast detail.
        </p>
      </div>
    );
  }

  if (effectiveLoading) {
    return (
      <div className="flex items-center justify-center rounded-card border border-card-bd bg-card py-16">
        <span className="text-sm text-text-3">Loading detail for &ldquo;{name}&rdquo;…</span>
      </div>
    );
  }

  if (!effectiveData) {
    return (
      <div className="flex items-center justify-center rounded-card border border-card-bd bg-card py-16">
        <span className="text-sm text-text-3">No data available for &ldquo;{name}&rdquo;.</span>
      </div>
    );
  }

  const { series = [], decomposition, contributors = [] } = effectiveData;
  // Prefer the backend's pre-computed cumulative figures (growth_eur / projected_spend)
  // so the header and the waterfall decomposition measure the same thing.
  // Fall back to the series-derived computation only when those fields are absent.
  const backendGrowth = effectiveData.growth_eur ?? null;
  const backendProjected = effectiveData.projected_spend ?? null;
  const { projected: seriesProjected, growth: seriesGrowth } =
    backendGrowth == null || backendProjected == null ? summaryFromSeries(series) : {};
  const projected = backendProjected ?? seriesProjected;
  const growth = backendGrowth ?? seriesGrowth;

  // Pick algorithm + smape from the first forecast point if available
  const algo = effectiveData.algorithm ?? null;
  const smape = effectiveData.smape ?? null;

  const sortedContributors = [...contributors].sort(
    (a, b) => Math.abs(b.delta_eur || 0) - Math.abs(a.delta_eur || 0)
  );

  return (
    <div className="space-y-4">
      {/* Header strip */}
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-card border border-card-bd bg-card px-5 py-4">
        <div>
          {(() => {
            const sn = formatSeriesName(name);
            return (
              <div className="flex items-center gap-1.5 text-base font-semibold text-text">
                <span>{sn.label}</span>
                {sn.hint && (
                  <span
                    className="cursor-help text-xs font-normal leading-none text-text-3"
                    data-tooltip-id="instantTooltip"
                    data-tooltip-content={sn.hint}
                    aria-label={sn.hint}
                  >
                    ⓘ
                  </span>
                )}
              </div>
            );
          })()}
          <div className="mt-0.5 text-xs text-text-3">
            {algo ? (
              <>
                <span className="font-medium text-text-2">{algo}</span>
                <span className="mx-1.5">·</span>
                <span
                  className={smape == null ? "italic text-text-3" : "tabular-nums"}
                >
                  {fmtSmape(smape)}
                </span>
              </>
            ) : (
              <span className="italic text-text-3">model not yet rated</span>
            )}
          </div>
        </div>
        <div className="flex gap-6 text-right">
          {projected != null && (
            <div>
              <div className="text-xs text-text-3">Projected</div>
              <div className="text-base font-semibold tabular-nums text-text">
                {fmtEur(projected)}
              </div>
            </div>
          )}
          {growth != null && (
            <div>
              <div className="text-xs text-text-3">Growth</div>
              <div
                className={`text-base font-semibold tabular-nums ${
                  growth > 0 ? "text-danger" : growth < 0 ? "text-success" : "text-text-2"
                }`}
              >
                {fmtEurSigned(growth)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Forecast chart — reuses the exec ForecastChart with the same series shape */}
      {series.length > 0 && (
        <ForecastChart series={series} horizon={horizon} />
      )}

      {/* Driver waterfall */}
      {decomposition && <DriverWaterfall decomposition={decomposition} />}

      {/* Top contributors */}
      {sortedContributors.length > 0 && (
        <div className="rounded-card border border-card-bd bg-card px-5 py-4">
          <div className="mb-3 text-xs font-medium uppercase tracking-wide text-text-3">
            Top contributors
          </div>
          <ul className="space-y-1.5">
            {sortedContributors.map((c, i) => {
              const { text: cname, hint } = contributorLabel(c);
              const delta = c.delta_eur;
              const positive = (delta || 0) > 0;
              return (
                <li
                  key={`${cname}-${i}`}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="flex items-center gap-1.5 text-text-2">
                    {cname}
                    {hint && (
                      <span
                        className="cursor-help text-xs leading-none text-text-3"
                        data-tooltip-id="instantTooltip"
                        data-tooltip-content={hint}
                        aria-label={hint}
                      >
                        ⓘ
                      </span>
                    )}
                  </span>
                  <span
                    className={`tabular-nums font-medium ${
                      delta != null && positive
                        ? "text-danger"
                        : delta != null && delta < 0
                        ? "text-success"
                        : "text-text-3"
                    }`}
                  >
                    {fmtEurSigned(delta)}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

SeriesDetail.propTypes = {
  name: PropTypes.string,
  horizon: PropTypes.number,
  data: PropTypes.shape({
    algorithm: PropTypes.string,
    smape: PropTypes.number,
    projected_spend: PropTypes.number,
    growth_eur: PropTypes.number,
    series: PropTypes.array,
    decomposition: PropTypes.shape({
      volume: PropTypes.number,
      price: PropTypes.number,
      other: PropTypes.number,
    }),
    contributors: PropTypes.array,
  }),
  isLoading: PropTypes.bool,
};
